export function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return 0
  }
  if (typeof value === 'number') {
    return isFinite(value) ? value : 0
  }
  const normalized = String(value).replace(/,/g, '').replace(/%/g, '').trim()
  const num = Number(normalized)
  if (isFinite(num)) {
    return num
  }
  const parsed = parseFloat(normalized)
  return isFinite(parsed) ? parsed : 0
}

export function toInteger(value) {
  return Math.round(toNumber(value))
}

export function toArray(value) {
  return Array.isArray(value) ? value : []
}

export function clampPercent(value) {
  const num = toNumber(value)
  if (num < 0) return 0
  if (num > 100) return 100
  return num
}

export function formatNumber(value, digits) {
  const precision = digits === undefined ? 2 : digits
  return toNumber(value).toLocaleString('zh-CN', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })
}

export function formatFixed(value, digits) {
  const precision = digits === undefined ? 2 : digits
  return toNumber(value).toFixed(precision)
}

function normalizeBusinessRow(row) {
  const item = row || {}
  return {
    ...item,
    revenue: toNumber(item.revenue),
    totalAmount: toNumber(item.totalAmount),
    avgOrderAmount: toNumber(item.avgOrderAmount),
    orderCount: toInteger(item.orderCount),
    percentage: toNumber(item.percentage)
  }
}

function normalizeTrendRow(row) {
  const item = row || {}
  return {
    ...item,
    revenue: toNumber(item.revenue),
    orderCount: toInteger(item.orderCount)
  }
}

export function normalizeBusinessData(data) {
  const source = data || {}
  return {
    ...source,
    totalRevenue: toNumber(source.totalRevenue),
    actualRevenue: toNumber(source.actualRevenue),
    payAmount: toNumber(source.payAmount),
    refundAmount: toNumber(source.refundAmount),
    orderAmount: toNumber(source.orderAmount),
    discountAmount: toNumber(source.discountAmount),
    freightAmount: toNumber(source.freightAmount),
    avgOrderValue: toNumber(source.avgOrderValue),
    rechargeAmount: toNumber(source.rechargeAmount),
    orderCount: toInteger(source.orderCount),
    refundCount: toInteger(source.refundCount),
    rechargeCount: toInteger(source.rechargeCount),
    channelBreakdown: toArray(source.channelBreakdown).map(normalizeBusinessRow),
    schoolStatistics: toArray(source.schoolStatistics).map(normalizeBusinessRow),
    storeStatistics: toArray(source.storeStatistics).map(normalizeBusinessRow),
    trendData: toArray(source.trendData).map(normalizeTrendRow)
  }
}

function normalizeMemberTrend(row) {
  const item = row || {}
  return {
    ...item,
    newMembers: toInteger(item.newMembers),
    activeMembers: toInteger(item.activeMembers),
    totalMembers: toInteger(item.totalMembers),
    growthRate: toNumber(item.growthRate)
  }
}

function normalizeTopSpender(row) {
  const item = row || {}
  return {
    ...item,
    totalSpending: toNumber(item.totalSpending),
    orderCount: toInteger(item.orderCount)
  }
}

function normalizePercentageRow(row) {
  const item = row || {}
  return {
    ...item,
    count: toInteger(item.count),
    percentage: toNumber(item.percentage)
  }
}

function normalizeWechatVisitData(data) {
  const source = data || {}
  const visitSummary = source.visitSummary || {}
  const userPortrait = source.userPortrait || {}
  return {
    ...source,
    visitSummary: {
      ...visitSummary,
      totalVisitUv: toInteger(visitSummary.totalVisitUv),
      totalVisitPv: toInteger(visitSummary.totalVisitPv),
      newUserCount: toInteger(visitSummary.newUserCount),
      avgVisitDepth: toNumber(visitSummary.avgVisitDepth)
    },
    userPortrait: {
      ...userPortrait,
      genderDistribution: toArray(userPortrait.genderDistribution).map(normalizePercentageRow),
      ageDistribution: toArray(userPortrait.ageDistribution).map(normalizePercentageRow),
      regionDistribution: toArray(userPortrait.regionDistribution).map(normalizePercentageRow)
    },
    visitTrend: toArray(source.visitTrend).map(item => ({
      ...item,
      visitUv: toInteger(item && item.visitUv),
      visitPv: toInteger(item && item.visitPv),
      newUserCount: toInteger(item && item.newUserCount)
    })),
    pageVisit: toArray(source.pageVisit).map(item => ({
      ...item,
      visitUv: toInteger(item && item.visitUv),
      visitPv: toInteger(item && item.visitPv),
      avgStayTime: toNumber(item && item.avgStayTime)
    })),
    visitRetain: toArray(source.visitRetain).map(item => ({
      ...item,
      visitDate: item && item.visitDate,
      newUserCount: toInteger(item && item.newUserCount),
      retainUserCount: toInteger(item && item.retainUserCount),
      retainRate: toNumber(item && item.retainRate)
    }))
  }
}

export function normalizeMemberData(data) {
  const source = data || {}
  return {
    ...source,
    totalMemberCount: toInteger(source.totalMemberCount),
    newMemberCount: toInteger(source.newMemberCount),
    activeMemberCount: toInteger(source.activeMemberCount),
    totalActiveMembers: toInteger(source.totalActiveMembers),
    growthRate: toNumber(source.growthRate),
    topSpenders: toArray(source.topSpenders).map(normalizeTopSpender),
    memberTrendData: toArray(source.memberTrendData).map(normalizeMemberTrend),
    wechatVisitData: normalizeWechatVisitData(source.wechatVisitData)
  }
}

export function normalizeProductRank(row) {
  const item = row || {}
  return {
    ...item,
    salesAmount: toNumber(item.salesAmount),
    salesQuantity: toInteger(item.salesQuantity),
    profit: toNumber(item.profit)
  }
}

function normalizeCategory(row) {
  const item = row || {}
  return {
    ...item,
    salesAmount: toNumber(item.salesAmount),
    salesQuantity: toInteger(item.salesQuantity),
    percentage: toNumber(item.percentage)
  }
}

export function normalizeProductData(data) {
  const source = data || {}
  const profitSummary = source.profitSummary || {}
  return {
    ...source,
    orderAmount: toNumber(source.orderAmount),
    discountAmount: toNumber(source.discountAmount),
    freightAmount: toNumber(source.freightAmount),
    refundAmount: toNumber(source.refundAmount),
    actualRevenue: toNumber(source.actualRevenue),
    totalSalesAmount: toNumber(source.totalSalesAmount),
    totalSalesQuantity: toInteger(source.totalSalesQuantity),
    totalProductCount: toInteger(source.totalProductCount),
    topSellingByAmount: toArray(source.topSellingByAmount).map(normalizeProductRank),
    topSellingByQuantity: toArray(source.topSellingByQuantity).map(normalizeProductRank),
    categoryDistribution: toArray(source.categoryDistribution).map(normalizeCategory),
    profitSummary: {
      ...profitSummary,
      totalProfit: toNumber(profitSummary.totalProfit),
      profitMargin: toNumber(profitSummary.profitMargin),
      topProfitProducts: toArray(profitSummary.topProfitProducts).map(normalizeProductRank)
    }
  }
}



// WEBPACK FOOTER //
// ./src/utils/dashboardData.js